// ────────────────────────────────────────────────────────────────
// GSelect.jsx
//
// Custom glassy dropdown that matches the dashboard panel aesthetic.
// Originally lived inside AdminDashboard.js — extracted here so the
// user-facing Dashboard can use the same component for its orders
// filter strip without re-implementing it.
//
// Props:
//   value     - currently selected value
//   onChange  - (value) => void
//   options   - [{ value, label }]
//   minWidth  - optional, defaults to 130px
// ────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const W = (a) => `rgba(255,255,255,${a})`;

export default function GSelect({ value, onChange, options, minWidth = 130 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sel = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '9px 14px',
          background:
            'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)',
          border: '1px solid rgba(255,255,255,0.09)',
          boxShadow:
            '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
          borderRadius: 8,
          color: W(0.65),
          fontSize: 13,
          fontFamily: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span>{sel?.label || value}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke={W(0.3)}
          strokeWidth="2.5"
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .15s',
            flexShrink: 0,
          }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute',
              zIndex: 200,
              top: 'calc(100% + 6px)',
              left: 0,
              right: 0,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.09)',
              background:
                'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow:
                '0 16px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '9px 14px',
                  textAlign: 'left',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  background:
                    value === opt.value
                      ? 'rgba(255,255,255,0.08)'
                      : 'none',
                  color: value === opt.value ? W(0.9) : W(0.5),
                  border: 'none',
                  transition: 'all .12s',
                }}
                onMouseEnter={(e) => {
                  if (value !== opt.value) {
                    e.currentTarget.style.background =
                      'rgba(255,255,255,0.05)';
                    e.currentTarget.style.color = W(0.8);
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== opt.value) {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.color = W(0.5);
                  }
                }}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
